/**
 * Agent-only: copy/paste passenger details between bookings.
 * Uses localStorage key agent_passengers_clipboard. No backend.
 */

const STORAGE_KEY = "agent_passengers_clipboard";
const CLIPBOARD_VERSION = 1;

/**
 * Fields we copy from each passenger (any other key on the object is also copied).
 * These are the main form fields; we still copy all enumerable own properties.
 */
const PASSENGER_COPY_KEYS = [
  "title",
  "gender",
  "firstname",
  "lastname",
  "cell",
  "email",
  "age",
  "kisSearchAge",
  "birthDate",
  "isMainContact",
];

function pickPassengerFields(pax) {
  if (!pax || typeof pax !== "object") return {};
  const out = {};
  for (const key of PASSENGER_COPY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(pax, key)) {
      out[key] = pax[key];
    }
  }
  // Copy any other own string/number/boolean (extra form fields)
  for (const key of Object.keys(pax)) {
    if (PASSENGER_COPY_KEYS.includes(key)) continue;
    const v = pax[key];
    if (v !== null && v !== undefined && (typeof v === "string" || typeof v === "number" || typeof v === "boolean")) {
      out[key] = v;
    }
  }
  return out;
}

/**
 * Serialize roomsAndPaxInfo into a clipboard-friendly structure (no room refs, no internal state).
 * @param {Array} roomsAndPaxInfo - current form state from context
 * @returns {Object} { version, rooms: [ { adults, kids, infants }, ... ] }
 */
export function passengersToClipboard(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
    return null;
  }
  const rooms = roomsAndPaxInfo.map((room) => ({
    adults: (room.adults || []).map(pickPassengerFields),
    kids: (room.kids || []).map(pickPassengerFields),
    infants: (room.infants || []).map(pickPassengerFields),
  }));
  return { version: CLIPBOARD_VERSION, rooms };
}

/**
 * Flatten clipboard or current form into a single list: [adult, adult, ..., kid, kid, ..., infant, ...]
 * Order: room0 adults, room0 kids, room0 infants, room1 adults, ...
 */
function flattenPassengers(rooms) {
  const list = [];
  (rooms || []).forEach((room) => {
    (room.adults || []).forEach((p) => list.push({ type: "adult", pax: p }));
    (room.kids || []).forEach((p) => list.push({ type: "kid", pax: p }));
    (room.infants || []).forEach((p) => list.push({ type: "infant", pax: p }));
  });
  return list;
}

/**
 * Apply clipboard passenger data into a deep clone of currentRoomsAndPaxInfo.
 * - Same number of passengers: fill by room/slot.
 * - Different: fill by flat order (first N passengers from clipboard into first N slots in form).
 * Does not change room structure (number of rooms, adults, kids, infants) or .room property.
 * @param {Object} clipboardData - from getPassengersClipboard()
 * @param {Array} currentRoomsAndPaxInfo - current form state
 * @returns {Array} new roomsAndPaxInfo to set in state
 */
export function pastePassengersIntoForm(clipboardData, currentRoomsAndPaxInfo) {
  if (!clipboardData || !clipboardData.rooms || !Array.isArray(currentRoomsAndPaxInfo)) {
    return currentRoomsAndPaxInfo;
  }
  const flatClip = flattenPassengers(clipboardData.rooms);
  if (flatClip.length === 0) return currentRoomsAndPaxInfo;

  const next = JSON.parse(JSON.stringify(currentRoomsAndPaxInfo));
  let clipIndex = 0;

  next.forEach((room) => {
    (room.adults || []).forEach((pax) => {
      if (clipIndex < flatClip.length) {
        const src = flatClip[clipIndex].pax;
        Object.keys(src).forEach((k) => {
          pax[k] = src[k];
        });
        clipIndex++;
      }
    });
    (room.kids || []).forEach((pax) => {
      if (clipIndex < flatClip.length) {
        const src = flatClip[clipIndex].pax;
        Object.keys(src).forEach((k) => {
          pax[k] = src[k];
        });
        clipIndex++;
      }
    });
    (room.infants || []).forEach((pax) => {
      if (clipIndex < flatClip.length) {
        const src = flatClip[clipIndex].pax;
        Object.keys(src).forEach((k) => {
          pax[k] = src[k];
        });
        clipIndex++;
      }
    });
  });

  return next;
}

/**
 * Hotel checkout passengerInfo shape is an array of rooms, each room is an array
 * of passenger slots. Store it in the same clipboard schema as package forms.
 */
export function passengerRoomArraysToClipboard(passengerInfo) {
  if (!Array.isArray(passengerInfo) || passengerInfo.length === 0) {
    return null;
  }
  const rooms = passengerInfo.map((room) => ({
    adults: (Array.isArray(room) ? room : []).map(pickPassengerFields),
    kids: [],
    infants: [],
  }));
  return { version: CLIPBOARD_VERSION, rooms };
}

export function pastePassengersIntoRoomArrays(clipboardData, currentPassengerInfo) {
  if (!clipboardData || !Array.isArray(clipboardData.rooms) || !Array.isArray(currentPassengerInfo)) {
    return currentPassengerInfo;
  }
  const flatClip = flattenPassengers(clipboardData.rooms);
  if (flatClip.length === 0) return currentPassengerInfo;

  const next = JSON.parse(JSON.stringify(currentPassengerInfo));
  let clipIndex = 0;

  next.forEach((room) => {
    if (!Array.isArray(room)) return;
    room.forEach((pax) => {
      if (clipIndex >= flatClip.length) return;
      const src = flatClip[clipIndex].pax;
      Object.keys(src).forEach((k) => {
        pax[k] = src[k];
      });
      clipIndex++;
    });
  });

  return next;
}

export function hasFilledPassengerRoomArrays(passengerInfo) {
  if (!Array.isArray(passengerInfo)) return false;
  return passengerInfo.some((room) =>
    Array.isArray(room) &&
    room.some(
      (pax) =>
        (pax?.firstname && String(pax.firstname).trim()) ||
        (pax?.lastname && String(pax.lastname).trim()) ||
        (pax?.email && String(pax.email).trim()) ||
        (pax?.cell && String(pax.cell).trim())
    )
  );
}

export function getPassengersClipboard() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && data.version === CLIPBOARD_VERSION && Array.isArray(data.rooms)) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
}

export function setPassengersClipboard(data) {
  try {
    if (data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    }
    localStorage.removeItem(STORAGE_KEY);
    return false;
  } catch {
    return false;
  }
}

/**
 * Check if current form has any filled passenger data (so we can show confirm before paste).
 */
export function hasFilledPassengerData(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return false;
  const flat = flattenPassengers(roomsAndPaxInfo);
  return flat.some(({ pax }) => (pax.firstname && pax.firstname.trim()) || (pax.lastname && pax.lastname.trim()) || (pax.email && pax.email.trim()) || (pax.cell && pax.cell.trim()));
}
